import {findFocusables} from '@udemy-v2/design-system-utils';
import {Modal} from '@udemy-v2/react-dialog-components';
import {Loader, TextSkeleton} from '@udemy-v2/react-reveal-components';
import classNames from 'classnames';
import React, {useRef, useState, useImperativeHandle, forwardRef, useEffect} from 'react';

import {Review as ReviewComponent} from '../review/review.react-component';
import {NoReviewsMessage, noReviewsMessage} from '../reviews.react-component';
import {SearchWidget} from '../review/search-widget.react-component';
import {ReviewsContext} from '../types';
import {ReviewSummaryWidget} from '../review/review-summary-widget.react-component';
import styles from './reviews-modal.module.scss';
import {
    useReviews,
    ReviewRequestConfig,
    UserFieldSelection,
    ReviewResponseFieldSelection,
    ReviewFieldSelection,
    ReviewOrdering,
} from '@/lib/hooks/use-reviews';
import {publishEvent, TrackImpression} from '@udemy-v2/event-tracking';
import {Review} from '@/types/review';
import {
    RatingsSummaryImpressionEvent,
    SeeMoreReviewsClickEvent,
} from '@/features/course-landing-page/events/events';
import {Button} from '@udemy-v2/react-core-components';
import {useI18n} from '@udemy-v2/i18n';
import {ReviewSkeleton} from '../review/review-skeleton.react-component';

interface ReviewsModalContainerProps {
    reviewsRatings?: ReviewsContext;
    frontendTrackingId?: string;
    reviewableObjectId: number;
    reviewableObjectType: string;
    isXsMax: boolean;
    isOpen: boolean;
    renderTitle: () => React.ReactNode;
    onClose: () => void;
    shouldUseTwoColumnLayout?: boolean;
}

export const ReviewsModalContainer = ({
    reviewsRatings,
    frontendTrackingId,
    reviewableObjectId,
    reviewableObjectType,
    isXsMax,
    isOpen,
    renderTitle,
    onClose,
    shouldUseTwoColumnLayout = false,
}: ReviewsModalContainerProps) => {
    // Set up config for useReviews
    const [searchText, setSearchText] = useState<string>('');
    const [selectedRating, setSelectedRating] = useState<number | undefined>(undefined);
    const reviewsConfig: ReviewRequestConfig = {
        courseId: reviewsRatings?.courseId?.toString() ?? '',
        pageSize: 12,
        isTextReview: searchText ? undefined : true,
        searchText: searchText || undefined,
        rating: selectedRating,
        fields: [ReviewFieldSelection.RATING],
        userFields: [UserFieldSelection.IMAGE, UserFieldSelection.DISPLAY_NAME],
        responseFields: [
            ReviewResponseFieldSelection.CONTENT,
            ReviewResponseFieldSelection.USER,
            ReviewResponseFieldSelection.CREATED,
            ReviewResponseFieldSelection.ID,
        ],
        ordering: [ReviewOrdering.REVIEW_SCORE_RANK, ReviewOrdering.CREATED_DESC],
    };

    const {
        data: reviews,
        isLoading,
        isLoadingMore,
        fetchNext,
        hasMore,
        ratingDistribution,
        averageRating,
    } = useReviews(reviewsConfig);

    const handleOnModalClose = () => {
        onClose();
        setSearchText('');
        setSelectedRating(undefined);
    };

    return (
        <Modal
            onClose={handleOnModalClose}
            className={styles['reviews-modal']}
            isOpen={isOpen}
            fullPage={isXsMax}
            title={renderTitle()}
        >
            <ReviewsModalContent
                reviews={reviews}
                isLoading={isLoading}
                isLoadingMore={isLoadingMore}
                fetchNext={fetchNext}
                hasMore={hasMore}
                ratingDistribution={ratingDistribution}
                averageRating={averageRating}
                searchText={searchText}
                setSearchText={setSearchText}
                selectedRating={selectedRating}
                setSelectedRating={setSelectedRating}
                reviewableObjectType={reviewableObjectType}
                shouldUseTwoColumnLayout={shouldUseTwoColumnLayout}
                reviewableObjectId={reviewableObjectId}
                frontendTrackingId={frontendTrackingId ?? ''}
                isXsMax={isXsMax}
            />
        </Modal>
    );
};

interface ReviewsModalContentProps {
    reviews: Review[];
    isLoading: boolean;
    isLoadingMore: boolean;
    fetchNext: () => Promise<void>;
    hasMore: boolean;
    ratingDistribution: {rating: number; count: number}[];
    averageRating: number;
    searchText: string;
    setSearchText: (text: string) => void;
    selectedRating: number | undefined;
    setSelectedRating: (rating: number | undefined) => void;
    reviewableObjectType: string;
    shouldUseTwoColumnLayout: boolean;
    reviewableObjectId: number;
    frontendTrackingId: string;
    isXsMax: boolean;
}

const ReviewsModalContent = ({
    reviews,
    isLoading,
    isLoadingMore,
    fetchNext,
    hasMore,
    frontendTrackingId,
    ratingDistribution,
    averageRating,
    searchText,
    setSearchText,
    selectedRating,
    setSelectedRating,
    reviewableObjectType,
    shouldUseTwoColumnLayout,
    reviewableObjectId,
    isXsMax,
}: ReviewsModalContentProps) => {
    const {gettext} = useI18n();
    const reviewsSectionRef = useRef<{focusFirstNewReview: () => boolean}>(null);
    const [firstNewReviewIndex, setFirstNewReviewIndex] = useState<number | null>(null);

    const totalDistributionCount = React.useMemo(() => {
        return ratingDistribution.reduce((acc, curr) => acc + curr.count, 0);
    }, [ratingDistribution]);

    // Focus the first new review when reviews load after pagination
    // Track previous reviews length to detect when new reviews are loaded
    const prevReviewsLengthRef = React.useRef(reviews.length);

    // Use effect to handle focus and state reset
    useEffect(() => {
        // Check if new reviews were loaded after pagination
        if (
            firstNewReviewIndex !== null &&
            reviews.length > firstNewReviewIndex &&
            reviews.length !== prevReviewsLengthRef.current
        ) {
            prevReviewsLengthRef.current = reviews.length;

            // Schedule focus attempt after render
            const timeoutId = requestAnimationFrame(() => {
                const focused = reviewsSectionRef.current?.focusFirstNewReview();
                if (focused) {
                    setFirstNewReviewIndex(null);
                }
            });
            return () => cancelAnimationFrame(timeoutId);
        }
    }, [reviews.length, firstNewReviewIndex]);

    if (!reviews) {
        return (
            <ReviewsModalContentLoadingSkeleton
                shouldUseTwoColumnLayout={shouldUseTwoColumnLayout}
            />
        );
    }

    const trackRatingsSummaryImpression = () => {
        if (frontendTrackingId) {
            publishEvent(
                new RatingsSummaryImpressionEvent({
                    courseTrackingId: frontendTrackingId,
                    reviewableObjectId,
                    reviewableObjectType,
                }),
            );
        }
    };

    const trackSeeMoreReviewsClick = () => {
        if (frontendTrackingId) {
            publishEvent(
                new SeeMoreReviewsClickEvent({
                    courseTrackingId: frontendTrackingId,
                    reviewableObjectId,
                    reviewableObjectType,
                }),
            );
        }
    };

    const onSeeMoreReviewsClicked = async () => {
        trackSeeMoreReviewsClick();
        setFirstNewReviewIndex(reviews.length); // index of the first new review
        await fetchNext();
    };

    return (
        <div
            className={classNames(styles['reviews-modal-content'], {
                [styles['reviews-two-column-layout']]: shouldUseTwoColumnLayout,
            })}
        >
            <div className={styles['reviews-controls']}>
                <TrackImpression trackFunc={trackRatingsSummaryImpression}>
                    <div className={styles['ratings-filter-container']}>
                        <ReviewSummaryWidget
                            showAverageRating={false}
                            averageRating={averageRating}
                            ratingDistribution={ratingDistribution}
                            ratingSize="medium"
                            selectedRating={selectedRating}
                            onRatingSelected={setSelectedRating}
                            totalDistributionCount={totalDistributionCount}
                            // TODO: Implement onSeen if needed
                        />
                    </div>
                </TrackImpression>
                <SearchWidget
                    onSubmit={() => {}}
                    value={searchText}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setSearchText(e.target.value)
                    }
                    onClear={() => setSearchText('')}
                    size="small"
                    submitButtonProps={{
                        className: styles['search-submit-button'],
                    }}
                />
            </div>
            <div className={styles['reviews-list-section']}>
                <ReviewsSection
                    ref={reviewsSectionRef}
                    reviews={reviews}
                    isLoading={isLoading}
                    reviewableObjectType={reviewableObjectType}
                    reviewableObjectId={reviewableObjectId}
                    firstNewReviewIndex={firstNewReviewIndex}
                    searchText={searchText}
                    frontendTrackingId={frontendTrackingId}
                    selectedRating={selectedRating}
                    isXsMax={isXsMax}
                />
                {!isLoading && hasMore && (
                    <Button
                        data-purpose="show-more-review-button"
                        disabled={isLoadingMore}
                        onClick={onSeeMoreReviewsClicked}
                        className={classNames(
                            styles['show-more-reviews-button'],
                            styles['full-width-button'],
                        )}
                        udStyle="secondary"
                    >
                        {isLoadingMore ? <Loader color="inherit" size="medium" /> : null}
                        {gettext('Show more reviews')}
                    </Button>
                )}
            </div>
        </div>
    );
};

interface ReviewsSectionProps {
    reviews: Review[];
    isLoading: boolean;
    reviewableObjectType: string;
    reviewableObjectId: number;
    firstNewReviewIndex: number | null;
    searchText?: string;
    frontendTrackingId: string;
    selectedRating: number | undefined;
    isXsMax: boolean;
}

type ReviewsSectionRef = {
    focusFirstNewReview: () => boolean;
};

const ReviewsSection = forwardRef<ReviewsSectionRef, ReviewsSectionProps>(
    (
        {
            reviews,
            isLoading,
            reviewableObjectType,
            reviewableObjectId,
            firstNewReviewIndex,
            searchText,
            frontendTrackingId,
            selectedRating,
            isXsMax,
        },
        ref,
    ) => {
        const firstNewReviewRef = useRef<HTMLDivElement>(null);
        const {gettext} = useI18n();

        useImperativeHandle(ref, () => ({
            focusFirstNewReview: () => {
                if (firstNewReviewRef.current) {
                    const focusableElements = findFocusables(firstNewReviewRef.current);
                    if (focusableElements.length > 0) {
                        focusableElements[0].focus();
                    } else {
                        firstNewReviewRef.current.focus();
                    }
                    return true;
                }
                return false;
            },
        }));

        if (isLoading) {
            return <ReviewsSectionLoadingSkeleton />;
        }

        const message = noReviewsMessage({rating: selectedRating, searchText}, gettext);

        return (
            <div className={styles['reviews-list-container']}>
                {!reviews || reviews.length === 0 ? (
                    <NoReviewsMessage message={message} />
                ) : (
                    <div className={styles['reviews-list']}>
                        {reviews.map((review, index) => {
                            const reviewRef =
                                index === firstNewReviewIndex ? firstNewReviewRef : null;
                            return (
                                <div key={review.id} ref={reviewRef}>
                                    <ReviewComponent
                                        review={review}
                                        reviewableObjectType={reviewableObjectType}
                                        reviewableObjectId={reviewableObjectId}
                                        frontendTrackingId={frontendTrackingId}
                                        searchedTerm={searchText}
                                        className={styles.review}
                                        instructorResponseVisibility="visible"
                                        isDesktopLayout={!isXsMax}
                                        shouldUseInlineShowMore={true}
                                        isModal={true}
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    },
);

ReviewsSection.displayName = 'ReviewsSection';

const ReviewsModalContentLoadingSkeleton = ({
    shouldUseTwoColumnLayout,
}: {
    shouldUseTwoColumnLayout: boolean;
}) => (
    <div
        className={classNames(styles['reviews-modal-content'], {
            [styles['reviews-two-column-layout']]: shouldUseTwoColumnLayout,
        })}
    >
        <div className={styles['reviews-controls']}>
            <TextSkeleton lineCountPerParagraph={5} />
        </div>
        <ReviewsSectionLoadingSkeleton />
    </div>
);

const ReviewsSectionLoadingSkeleton = () => (
    <div className={styles['reviews-list']}>
        {[...Array(4)].map((el, i) => (
            <div className={styles['reviews-list-item']} key={i}>
                <ReviewSkeleton />
            </div>
        ))}
    </div>
);
